import React, { useState, useEffect } from "react";
import { useField, useFormikContext, FieldHookConfig } from "formik";
import { FileSpreadsheet } from "lucide-react";
import { Button } from "../../../../components/src/Button";
import { TimeSeriesItemInput } from "../../types.generated";
import { cloneDeep } from "lodash";
import { exportToXlsx2 } from "../../../../components/src/common/exportToXlsx2";
import { TimeSeriesItem } from "../../../../common/src/types.generated";
import { flexRender, SortingState, useTable } from "@tanstack/react-table";
import { Filter } from "../../../../components/src/react-table/ReactTableFilters";
import { reactTableFeatures } from "../../../../components/src/react-table/features";

const EditableCell = ({ cellProps }: { cellProps: any }): React.ReactElement => {
    const initialValue = cellProps.getValue();
    const [value, setValue] = useState(initialValue);
    const rowData = cellProps.row.original;
    const rowId = cellProps.row.id;
    const columnId = cellProps.column.id;
    const updateData = cellProps.table.options.meta;
    const rowIndex = cellProps.row.index;

    useEffect(() => {
        const currentValue = cellProps.getValue();
        setValue(currentValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowData, columnId, rowId]);

    return (
        <input
            className="p-0 w-full"
            value={value}
            onChange={(e) => {
                setValue(e.target.value);
            }}
            onBlur={() => {
                updateData(rowIndex, columnId, value);
            }}
        />
    );
};

function Table({ columns, data, updateData, skipPageReset }) {
    const [sorting, setSorting] = useState<SortingState>([]);

    const defaultColumn: any = { size: 10 };

    const table = useTable({
        features: reactTableFeatures,
        data,
        columns,
        defaultColumn,
        state: {
            sorting
        },
        meta: updateData,
        autoResetPageIndex: !skipPageReset,
        initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
        onSortingChange: setSorting
    });
    return (
        <>
            <table className="w-full" style={{ tableLayout: "auto" }}>
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id} className="tr" role="row">
                            {headerGroup.headers.map((header, headerIndex) => (
                                <th
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    style={{ width: header.getSize() }}
                                    className={`th ${headerIndex > 0 ? "border-l border-transparent" : ""}`}
                                    role="columnheader"
                                >
                                    {header.isPlaceholder ? null : (
                                        <div
                                            className="header-content"
                                            title="Toggle SortBy"
                                            {...{
                                                className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                                                onClick: header.column.getToggleSortingHandler()
                                            }}
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {{
                                                asc: " ▼",
                                                desc: " ▲"
                                            }[header.column.getIsSorted() as string] ?? null}
                                        </div>
                                    )}
                                    {!header.isPlaceholder && header.column.getCanFilter() ? (
                                        <div>
                                            <Filter column={header.column} table={table} />
                                        </div>
                                    ) : null}
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody role="rowgroup">
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="tr relative" role="row">
                            {row.getVisibleCells().map((cell) => (
                                <td
                                    key={cell.id}
                                    className="td relative overflow-visible"
                                    role="cell"
                                    style={{ width: cell.column.getSize() }}
                                >
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>

            <div className="mt-2 flex justify-between">
                <button className="button" onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()} title="First">
                    &laquo;
                </button>{" "}
                <span className="p-1">
                    Page:{" "}
                    <input
                        type="number"
                        min={1}
                        max={table.getPageCount()}
                        value={table.state.pagination.pageIndex + 1}
                        onChange={(e) => {
                            const page = e.target.value ? Number(e.target.value) - 1 : 0;
                            table.setPageIndex(page);
                        }}
                        style={{ maxWidth: "70px" }}
                    />{" "}
                    of {table.getPageCount()}
                </span>
                <button onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()} title="Last">
                    &raquo;
                </button>
                <select
                    value={table.state.pagination.pageSize}
                    onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                    }}
                    className="ml-auto p-1"
                >
                    {[20, 40, 60, 80, 100].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                            Show {pageSize}
                        </option>
                    ))}
                </select>
            </div>
        </>
    );
}

type ArrayPagedTableFieldType = FieldHookConfig<TimeSeriesItemInput[]> & {
    label: string;
    className: string;
    disabled?: boolean;
    onChange?: any;
};

export const ArrayPagedTableField = ({ label, className, disabled, onChange, ...props }: ArrayPagedTableFieldType): React.ReactElement => {
    const [field] = useField(props);
    const [skipPageReset, setSkipPageReset] = React.useState(false);

    const { setFieldValue } = useFormikContext();

    const onAddNew = () => {
        setSkipPageReset(false);
        const update = cloneDeep(field.value);
        const today = new Date().toISOString().split("T")[0];
        update.unshift({ date: today, value: 0.0 });
        setFieldValue(field.name, update);
        if (onChange) {
            onChange(update);
        }
    };

    const onDuplicate = React.useCallback(
        (e) => {
            setSkipPageReset(true);
            const update = cloneDeep(field.value);
            // find index of
            const ndx = update.findIndex((x) => x.date === e.date);
            // insert new duplicated item
            update.splice(ndx, 0, { date: e.date, value: e.value });
            setFieldValue(field.name, update);
            if (onChange) {
                onChange(update);
            }
        },
        [setFieldValue, field, onChange]
    );

    const onDelete = React.useCallback(
        (item) => {
            setSkipPageReset(true);
            const update = cloneDeep(field.value);
            const index = update.findIndex((x) => x.date === item.date && x.value === item.value);
            if (index > -1) {
                update.splice(index, 1);
                setFieldValue(field.name, update);
                if (onChange) {
                    onChange(update);
                }
            }
        },
        [setFieldValue, field, onChange]
    );

    const columns = React.useMemo(
        () => [
            {
                header: "Date",
                accessorKey: "date",
                size: 120,
                cell: (cellProps) => <EditableCell cellProps={cellProps} />
            },
            {
                header: "Value",
                accessorKey: "value",
                cell: (cellProps) => <EditableCell cellProps={cellProps} />,
                size: 80
            },
            {
                header: " ",
                size: 40,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Button
                            title="Duplicate"
                            variant="secondary"
                            size="sm"
                            className="relative z-10 pointer-events-auto"
                            style={{ minWidth: "28px", width: "28px", padding: "0" }}
                            disabled={disabled}
                            onClick={() => {
                                onDuplicate(row.original);
                            }}
                        >
                            +
                        </Button>
                    );
                }
            },
            {
                header: "  ",
                size: 40,
                cell: (cellProps) => {
                    const { row } = cellProps;
                    return (
                        <Button
                            title="Delete"
                            variant="danger"
                            size="sm"
                            className="relative z-10 pointer-events-auto"
                            style={{ minWidth: "28px", width: "28px", padding: "0" }}
                            disabled={disabled}
                            onClick={() => {
                                onDelete(row.original);
                            }}
                        >
                            ×
                        </Button>
                    );
                }
            }
        ],
        [onDelete, onDuplicate, disabled]
    );

    const updateData = (rowIndex, columnId, value) => {
        setSkipPageReset(true);
        const updatedData = field.value.map((row, index) => {
            if (index === rowIndex) {
                return {
                    ...field.value[rowIndex],
                    [columnId]: value
                };
            }
            return row;
        });
        setFieldValue(field.name, updatedData);
        if (onChange) {
            onChange(updatedData);
        }
    };

    const download = (data) => {
        const rows = data.map((r: TimeSeriesItem) => {
            return {
                date: r.date,
                value: r.value
            };
        });
        exportToXlsx2(rows, "timeserieItems.xlsx");
    };

    return (
        <div className={"mb-4 w-full" + (className ? " " + className : "")}>
            <span
                style={{ display: "inline-block", width: "20px", height: "20px", cursor: "pointer", float: "right" }}
                title="Download"
                onClick={() => {
                    download(field.value);
                }}
            >
                <FileSpreadsheet className="Excel" />
            </span>
            <label>{label}</label>
            <span className="mx-2">{field.value.length} items</span>
            <button type="button" onClick={onAddNew} className="btn btn-secondary btn-sm">
                +
            </button>
            <div className="w-full">
                <Table columns={columns} data={field.value} updateData={updateData} skipPageReset={skipPageReset} />
            </div>
        </div>
    );
};
